import { mutationOptions, type QueryClient } from '@tanstack/react-query';
import type { DietEntry } from '@diet/types';
import {
  createEntry,
  deleteEntry,
  updateEntry,
} from '@diet/data/diet-repository';
import { saveSettings } from '@diet/data/diet-settings-repository';
import { entriesOptions, settingsOptions } from '@diet/data/queries';

/*
 * 寫入定義的單一來源，與 `queries.ts` 同一個形狀。
 *
 * 每支都收 `QueryClient` 而不是在元件裡才接線：這些寫入的成功處理**就是**改寫某把
 * 快取，而那把 key 來自 `queries.ts`。定義放在同一層，「寫哪一把 key」與「那把 key
 * 長什麼樣」就不會分居兩地——這正是 `queryOptions` 要解決的問題，寫入端同理。
 *
 * 不設 `mutationKey`：那個欄位是給 `useMutationState` 事後查詢用的（例如全域的
 * 「儲存中…」指示器），本專案沒有這種需求，加了只是多一組要維護的字串。
 */

export function addEntryMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: createEntry,
    onSuccess: (entry) => {
      writeEntries(queryClient, (entries) => [...entries, entry]);
    },
  });
}

export function editEntryMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: updateEntry,
    onSuccess: (entry) => {
      writeEntries(queryClient, (entries) =>
        entries.map((item) => (item.id === entry.id ? entry : item)),
      );
    },
  });
}

export function removeEntryMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: deleteEntry,
    onSuccess: (id) => {
      writeEntries(queryClient, (entries) =>
        entries.filter((entry) => entry.id !== id),
      );
    },
  });
}

export function saveSettingsMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: saveSettings,
    onSuccess: (settings) => {
      queryClient.setQueryData(settingsOptions().queryKey, settings);
    },
  });
}

/**
 * 就地改寫紀錄快取。
 *
 * 寫入成功後直接把結果併回快取，而不是 `invalidateQueries` 重抓：那會為了一筆
 * 新增再拉一次 90 天的清單，使用者則要盯著剛記下的東西等它出現。後端回傳的內容
 * 就是這筆紀錄的真實樣貌（id 由它產生），併進去即為正確狀態。
 *
 * key 取自 `entriesOptions()`，型別隨之推導——不需要手抄 key，也不需要標泛型。
 *
 * 快取還不存在時什麼都不做——那代表初次載入還沒完成，等它回來自然就是最新的。
 */
function writeEntries(
  queryClient: QueryClient,
  update: (entries: DietEntry[]) => DietEntry[],
): void {
  queryClient.setQueryData(entriesOptions().queryKey, (entries) =>
    entries ? update(entries) : entries,
  );
}
